import React, {useEffect, useState} from "react";
import { UpdateFieldFn, Golfer } from "../types/types";
import { useNavigate, useLocation } from "react-router-dom";
import { checkEmailExists } from "../api/api-hook";
import { normalizeInput, formatDOB, convertFromISOToUS, isValidDOB, isUnder25 } from "../utils/utils";
import Footer from "./shared/Footer";
import { usePageContent } from "../api/usePageContent";

interface Step3Props {
  formData: Golfer;
  updateField: UpdateFieldFn;
  resetStep: (step: number) => void;
}


interface LocationState {
  golfer?: Golfer;
}

const Step3: React.FC<Step3Props> = ({ formData, updateField, resetStep }) => {
  const { title: pageTitle, overview: pageOverview } = usePageContent("step3");
  const navigate = useNavigate();
  const location = useLocation();
  const selectedContact = location.state.golfer;
  const [dobError, setDobError] = useState<string>("");
  const [ageRuleError, setAgeRuleError] = useState<string>("");
  const [emailStatus, setEmailStatus] = useState<
    "idle" | "checking" | "exists" | "available" | "error"
  >("idle");
  const [emailMessage, setEmailMessage] = useState<string>("");

  const state = (location.state || {}) as LocationState;

  const [golfer, setGolfer] = useState<Golfer | null>(
    state.golfer ?? null
  );

  console.log(location.state)

  const handleFieldChange = (field: keyof Golfer) => 
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const value = e.target.value;
  
      // 1. update your global form data
      updateField(field, value);

      // 2. also keep local golfer in sync
      setGolfer((prev) => {
        const base = prev ?? {};
  
        // map FormData field name ⇒ Golfer field name
        if (field === "firstName") {
          return { ...base, firstName: value };
        }
        if (field === "lastName") {
          return { ...base, lastName: value };
        }
        if (field === "email") {
          return { ...base, email: value };
        }
        if (field === "phone") {
          return { ...base, phone: value };
        }
        if (field === "sex") {
          return { ...base, sex: value };
        }
        if (field === "date_of_birth") {
          return { ...base, date_of_birth: value };
        }
  
        return base;
      });
    };



  const formatPhoneNumber = (event:React.KeyboardEvent<HTMLInputElement>) => { 

    const result = normalizeInput(event.currentTarget.value || '');
    updateField("phone", result);
    
  }


  const handleDOBChange = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const formatted = formatDOB(e.currentTarget.value);
    updateField("date_of_birth", formatted);
  
    // Only validate once full MM/DD/YYYY is present
    if (formatted.length !== 10) {
      setAgeRuleError("Please enter a valid date of birth (MM/DD/YYYY)");
      return;
    }
  
    // 1️ Valid date check
    if (!isValidDOB(formatted)) {
      setAgeRuleError("Please enter a valid date of birth (MM/DD/YYYY)");
      return;
    }
  
    // 2️ Under-25 check (ONLY for Bronze 18–25)
    const isBronze1825 =
      (formData.vip_package || "").toLowerCase() === "bronze-18-25";
  
    if (isBronze1825 && !isUnder25(formatted)) {
      setAgeRuleError("This Bronze option is only available for golfers under 25.");
      return;
    }
  
    //  All good
    setAgeRuleError("");
  };
  

  const handleEmailBlur = async () => {
    const email = (formData.email as string) || "";

    if (!email) {
      setEmailStatus("idle");
      setEmailMessage("");
      return;
    }

    // simple email sanity check (optional)
    const emailPattern = /\S+@\S+\.\S+/;
    if (!emailPattern.test(email)) {
      setEmailStatus("error");
      setEmailMessage("Please enter a valid email address.");
      return;
    }

    setEmailStatus("checking");

    const response_data = await checkEmailExists(email || '');
    
      const data = JSON.parse(response_data || '');

    if (data.error) {
      setEmailStatus("error");
      setEmailMessage("Could not verify email right now. Please continue.");
      return;
    }


    if (data.exists && data.contact.id != location.state.golfer.id) {
      setEmailStatus("error");
      setEmailMessage("That email is already taken! Please contact us if you believe there is an issue.");
    } else if (data.exists && data.contact.id == state.golfer?.id || "") {
      setEmailStatus("idle");
      setEmailMessage("");
    } else {
      setEmailStatus("available");
      setEmailMessage("");
    }
  };


  useEffect(() => {
    if (state.golfer) {
      updateField("id", state.golfer?.id as any || "");
      updateField("firstName", state.golfer?.firstName || "");
      updateField("lastName", state.golfer?.lastName || "");
      updateField("email", state.golfer?.email || "");
      updateField("phone", normalizeInput(state.golfer?.phone));
      updateField("sex", state.golfer?.sex || "");
      if (state.golfer.id) {
        updateField("id", state.golfer.id as any || '');
      }
      if (state.golfer?.date_of_birth && state.golfer?.date_of_birth.includes("-")) {
        updateField("date_of_birth", convertFromISOToUS(state.golfer?.date_of_birth));
      } else {
        updateField("date_of_birth", (state.golfer?.date_of_birth || ""));
      }
      updateField("address", state.golfer?.address || "");
      updateField("street_address_2", state.golfer?.street_address_2 || "");
      updateField("city", state.golfer?.city || "");
      updateField("state", state.golfer?.state || "");
      updateField("zip", state.golfer?.zip || "");

      updateField("bagTag", !!state.golfer?.bagTag);
      updateField("donation", !!state.golfer?.donation);
      updateField("personalization", state.golfer?.personalization || '');
      updateField("donation_amount", state.golfer?.donation_amount || 0);
      
      updateField("vip_card_type", state.golfer?.vip_card_type || "");
      updateField("vip_package", state.golfer?.vip_package || "");
      updateField("ghinNumber", state.golfer?.ghinNumber || "");
      updateField("product_id", state.golfer?.product_id || "");
      
    }
  }, []); 



  useEffect(() => {
    const isBronze1825 = (formData.vip_package || "").toLowerCase() === "bronze-18-25";
  
    if (!isBronze1825) {
      setAgeRuleError("");
      return;
    }
  
    // only enforce if DOB is fully typed
    if ((formData.date_of_birth || "").length !== 10) {
      setAgeRuleError("Date of birth is required for the Bronze (18–25) option.");
      return;
    }
  
    // you already check "isValidDOB" elsewhere
    if (!isValidDOB(formData.date_of_birth || "")) {
      setAgeRuleError("Please enter a valid date of birth (MM/DD/YYYY).");
      return;
    }
  
    if (!isUnder25(formData.date_of_birth || "")) {
      setAgeRuleError("This Bronze (under 25) option is only available for golfers under 25.");
      return;
    }
  
    setAgeRuleError("");
  }, [formData.vip_package, formData.date_of_birth]);
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
  
    // build an updated golfer from the latest formData if you want to be explicit:
      const updatedGolfer: Golfer = {
        ...(golfer ?? {}),
        firstName: formData.firstName || "",
        lastName: formData.lastName || "",
        email: formData.email || "",
        phone: formData.phone || "",
        sex: formData.sex || "",
        date_of_birth: formData.date_of_birth || "",
        address: formData.address || "",
        street_address_2: formData.street_address_2 || "",
        city: formData.city || "",
        state: formData.state || "",
        zip: formData.zip || "",
        id: formData.id as any || "",
        bagTag: formData.bagTag || false,
        donation: formData.donation || false,
        personalization: formData.personalization || "",
        donation_amount: formData.donation_amount || 0,
        vip_package: formData.vip_package || "",
        vip_card_type: formData.vip_card_type || "",
        ghinNumber: formData.ghinNumber || "",
        product_id: formData.product_id || ""
        
      };
  
  
  
    navigate("/step4", {
      state: {
        ...state,            // keep anything else (admin, etc.)
        golfer: updatedGolfer,
      },
    });
  };
  






  return (
    <div className="container mt-5">

    <section className="vip-step3 container my-5">
  <div className="row justify-content-center">

  <div className="col-lg-12">

{/* Page Heading */}
<h3 className="mb-3 text-uppercase">
  {pageTitle || "Purchase a VIP Card"}
</h3>
{(pageOverview !== undefined ? pageOverview : "The VIP Card offers golfers exclusive discounted rates at approximately 200 courses throughout Virginia and West Virginia. A portion of the proceeds go to support the VSGA-VIP Scholarship Foundation.") && (
  <p className="mb-4">
    {pageOverview || "The VIP Card offers golfers exclusive discounted rates at approximately 200 courses throughout Virginia and West Virginia. A portion of the proceeds go to support the VSGA-VIP Scholarship Foundation."}
  </p>
)}

{/* Grey Bar Section Title */}
<div
            className="bg-light border px-3 py-2 mb-4 text-uppercase fw-semibold"
            style={{ fontSize: "0.9rem" }}
          >
            Personal Information
          </div>

          {/* Subtitle */}
          <p className="text-start mb-4">
            Below is the information we have on record for your account. Please update any information that has changed. When you have finished, click the 'Next' button to complete the form. 
          </p>
        
        {golfer?.ghinNumber != "" ? 
        <>
          


<div className="alert alert-light d-flex align-items-center" role="alert">
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-info-circle" viewBox="0 0 16 16" id="circle">
    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
    <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0"/>
  </svg>
  
  <div className="px-2">NOTE: Personal information updated on this form will update your GHIN profile.</div>
</div>
</>  
: ''}
       

</div>



    <div className="col-lg-12">
      
      <div className="">
        <div className="card-body">

         <form id="step3-form" className="row gy-3" onSubmit={handleSubmit}>

           
            <div className="col-md-6">
              <label className="form-label" htmlFor="firstName">First Name</label>
              <input 
                type="text"
                id="firstName"
                name="firstName"
                className="form-control"
                placeholder="Enter First Name"
                value={formData.firstName}
                onChange={handleFieldChange("firstName")}
                required
              />
            </div>

   
            <div className="col-md-6">
              <label className="form-label" htmlFor="lastName">Last Name</label>
              <input 
                type="text"
                id="lastName"
                name="lastName"
                className="form-control"
                placeholder="Enter Last Name"
                value={formData.lastName}
                onChange={handleFieldChange("lastName")}
                required
              />
            </div>

           
            <div className="col-md-6">
              <label className="form-label" htmlFor="email">Email</label>
              <input 
                type="email"
                id="email"
                name="email"
                className="form-control"
                placeholder="Enter a valid email"
                value={formData.email}
                onBlur={handleEmailBlur}
                onChange={handleFieldChange("email")}
                pattern="[\-a-zA-Z0-9_+\}\{'?]+(\.[\-a-zA-Z0-9_+\}\{'?]+)*@[a-zA-Z0-9_][\-a-zA-Z0-9_]*(\.[\-a-zA-Z0-9_]+)*\.[a-zA-Z]*(:[0-9]{1,5})?"
                required
              />
              {emailStatus !== "idle" && (
            <small
              className={
                "form-text " +
                (emailStatus === "error"
                  ? "text-danger"
                  : emailStatus === "exists"
                  ? "text-success"
                  : "text-muted")
              }
            >
              {emailMessage}
            </small>
          )}
            </div>

     
            <div className="col-md-6">
              <label className="form-label" htmlFor="phone">Phone</label>
              <input 
                type="tel"
                id="phone"
                name="phone"
                className="form-control"
                placeholder="(555) 555-5555"
                value={formData.phone}
                onChange={handleFieldChange("phone")}
                pattern="[0-9\(\)\-\s]{14,14}"
                onKeyUp={formatPhoneNumber} 
                minLength={14} 
                maxLength={14}
                required
              />
            </div>


            <div className="col-md-6">
              <label className="form-label" htmlFor="gender">Gender</label>
              <select
                id="sex"
                name="sex"
                className="form-select"
                value={formData.sex || ""}
                onChange={handleFieldChange("sex")}
                required
              >
                <option value="">Select gender...</option>
                <option value="M">Male</option>
                <option value="F">Female</option>
              </select>
            </div>

   
            <div className="col-md-6">
              <label className="form-label" htmlFor="age">Date of Birth</label>
              <input 
                type="text"
                id="date_of_birth"
                name="date_of_birth"
                className={`form-control ${dobError ? "is-invalid" : ""}`}
                placeholder="MM/DD/YYYY"
                value={formData.date_of_birth}
                onChange={handleFieldChange("date_of_birth")}
                onKeyUp={handleDOBChange}
                required
             />
             
             {ageRuleError && <div className="text-danger small mt-1">{ageRuleError}</div>}
            </div>

        
            

        
            <div className="col-12 d-flex justify-content-between mt-4">
            <button
                type="button"
                id="step4-back"
                className="btn btn-link text-decoration-none px-0"
                onClick={() => {
                  resetStep(2);  // clear step 3 values
                  navigate("/step2", { state: { golfer: null } });
                }}
              >
                ← Back
              </button>

              <button 
                type="submit"
                className="btn btn-primary px-4"
                disabled={
                  emailStatus === "error" ||
                  emailStatus === "checking" ||
                  dobError !== "" ||
                  ageRuleError !== ""
                }
              >
                Next
              </button>
            </div>

          </form>

        </div>
      </div>



    </div>
  </div>
  {/* FOOTER NOTE */}
  <Footer />
</section>


    </div>
  );
};

export default Step3;
